<template>
  <div dir="rtl" class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold mb-6 text-customHafadhColor">نظام إرسال الإشعارات</h1>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- إرسال عام (لكل المستخدمين أو حسب الدور) -->
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">إرسال إشعار عام</h2>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نوع الإشعار</label>
            <select v-model="broadcast.type" class="input">
              <option value="announcement">إعلان</option>
              <option value="system">نظام</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
            <input v-model="broadcast.title" type="text" class="input" placeholder="عنوان الإشعار" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">المحتوى</label>
            <textarea v-model="broadcast.message" rows="4" class="input" placeholder="محتوى الإشعار"></textarea>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">فلترة حسب الدور (اختياري)</label>
              <select v-model="broadcast.userRole" class="input">
                <option value="">الكل</option>
                <option value="STUDENT">الطلاب</option>
                <option value="TEACHER">المعلمون</option>
                <option value="ADMIN">المشرفون</option>
              </select>
            </div>

            <div v-if="broadcast.type === 'system'">
              <label class="block text-sm font-medium text-gray-700 mb-1">نوع النظام</label>
              <select v-model="broadcast.systemType" class="input">
                <option value="MAINTENANCE">صيانة</option>
                <option value="FEATURE_UPDATE">تحديث ميزة</option>
                <option value="SYSTEM">عام</option>
              </select>
            </div>
          </div>

          <div class="flex justify-end">
            <button class="btn" :disabled="sending.broadcast || !isBroadcastValid" @click="sendBroadcast">
              <span v-if="sending.broadcast" class="loader"></span>
              <span v-else>إرسال</span>
            </button>
          </div>

          <p v-if="result.broadcast" :class="['mt-2 text-sm', result.broadcast.success ? 'text-green-600' : 'text-red-600']">
            {{ result.broadcast.message }}
          </p>
        </div>
      </div>

      <!-- إرسال مباشر (لمستخدم محدد) -->
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">إرسال مباشر لمستخدم محدد</h2>

        <div class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">طريقة الإرسال</label>
              <select v-model="direct.method" class="input">
                <option value="byUser">حسب المستخدم (ID)</option>
                <option value="byToken">حسب FCM Token</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">نوع الإشعار</label>
              <select v-model="direct.type" class="input">
                <option value="welcome">ترحيب (ثابت)</option>
                <option value="call_test">اختبار اتصال (جلسة)</option>
                <option value="test">اختبار (بالرمز)</option>
              </select>
            </div>
          </div>

          <div v-if="direct.method === 'byUser'" class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">الدور</label>
                <select v-model="direct.userRole" class="input" @change="loadDirectUsers">
                  <option value="STUDENT">طالب</option>
                  <option value="TEACHER">معلم</option>
                  <option value="ADMIN">مشرف</option>
                </select>
              </div>
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">اختر المستخدم</label>
                <select v-model="direct.selectedUserId" class="input" @change="onSelectUser" :disabled="usersLoading">
                  <option value="" disabled>— اختر —</option>
                  <option v-for="u in users" :key="u.id" :value="String(u.id)">
                    {{ u.name }} (ID: {{ u.id }})
                  </option>
                </select>
                <p v-if="usersLoading" class="text-xs text-gray-500 mt-1">جاري تحميل القائمة...</p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المعرف (ID)</label>
                <input v-model="direct.userId" type="text" class="input" placeholder="مثال: 123" readonly />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">الاسم</label>
                <input v-model="direct.userName" type="text" class="input" placeholder="اسم المستخدم" readonly />
              </div>
            </div>
            <div v-if="direct.type === 'call_test'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">عنوان الاتصال التجريبي</label>
                <input v-model="direct.title" type="text" class="input" placeholder="مثال: اتصال تجريبي" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">نص الإشعار</label>
                <input v-model="direct.message" type="text" class="input" placeholder="مثال: اختبار اتصال VoIP" />
              </div>
            </div>
          </div>

          <div v-if="direct.method === 'byToken'" class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">FCM Token</label>
              <textarea v-model="direct.fcmToken" rows="3" class="input" placeholder="أدخل رمز FCM للمستخدم"></textarea>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
                <input v-model="direct.title" type="text" class="input" placeholder="عنوان الاختبار" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">المحتوى</label>
                <input v-model="direct.message" type="text" class="input" placeholder="محتوى الاختبار" />
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <button class="btn" :disabled="sending.direct || !isDirectValid" @click="sendDirect">
              <span v-if="sending.direct" class="loader"></span>
              <span v-else>إرسال</span>
            </button>
          </div>

          <p v-if="result.direct" :class="['mt-2 text-sm', result.direct.success ? 'text-green-600' : 'text-red-600']">
            {{ result.direct.message }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'AdminNotifications',
  data() {
    return {
      sending: {
        broadcast: false,
        direct: false,
      },
      result: {
        broadcast: null,
        direct: null,
      },
      usersLoading: false,
      users: [],
      broadcast: {
        type: 'announcement', // announcement | system
        title: '',
        message: '',
        userRole: '', // optional filter
        systemType: 'SYSTEM',
      },
      direct: {
        method: 'byUser', // byUser | byToken
        type: 'welcome', // welcome (server) | call_test (session) | test (public)
        userId: '',
        userName: '',
        userRole: 'STUDENT',
        selectedUserId: '',
        fcmToken: '',
        title: 'اختبار اتصال',
        message: 'هذا إشعار اتصال تجريبي',
      },
    };
  },
  computed: {
    isBroadcastValid() {
      if (!this.broadcast.title || !this.broadcast.message) return false;
      if (this.broadcast.type === 'system' && !this.broadcast.systemType) return false;
      return true;
    },
    isDirectValid() {
      if (this.direct.method === 'byUser') {
        if (!this.direct.userId || !this.direct.userName || !this.direct.userRole) return false;
        if (this.direct.type === 'welcome') return true;
        if (this.direct.type === 'call_test') return !!(this.direct.title && this.direct.message);
        return false;
      }
      if (this.direct.method === 'byToken') {
        return !!(this.direct.fcmToken && this.direct.title && this.direct.message && this.direct.type === 'test');
      }
      return false;
    },
    baseUrl() {
      return process.env.VUE_APP_SERVICE_URL || '';
    },
    authHeaders() {
      return { 'X-Authorization': localStorage.getItem('token') };
    }
  },
  methods: {
    async loadDirectUsers() {
      try {
        this.usersLoading = true;
        this.users = [];

        let endpoint = '';
        if (this.direct.userRole === 'STUDENT') {
          endpoint = url.get_enabled_students; // قائمة الدارسين المفعّلين
        } else if (this.direct.userRole === 'TEACHER') {
          endpoint = url.get_teachers; // قائمة المعلمين
        } else if (this.direct.userRole === 'ADMIN') {
          endpoint = url.get_all_admins; // قائمة الإداريين
        }
        if (!endpoint) return;

        const res = await axios.get(this.baseUrl + endpoint, { headers: this.authHeaders });
        const raw = res?.data?.result?.data || res?.data?.data || res?.data || [];

        const toName = (it) => it.fullName || it.name || (it.firstName ? `${it.firstName} ${it.lastName || ''}`.trim() : (it.username || 'غير معروف'));
        const toId = (it) => it.id || it.userId || it.studentId || it.teacherId || it.adminId;
        const toPhone = (it) => it.phone || it.mobile || it.msisdn || '';

        this.users = Array.isArray(raw) ? raw.map(it => ({ id: toId(it), name: toName(it), phone: toPhone(it) })).filter(u => !!u.id) : [];

        // إعادة ضبط الاختيار
        this.direct.selectedUserId = '';
        this.direct.userId = '';
        this.direct.userName = '';
      } catch (e) {
        // صامت: عرض القائمة المنسدلة اختياري
        this.users = [];
      } finally {
        this.usersLoading = false;
      }
    },
    onSelectUser() {
      const chosen = this.users.find(u => String(u.id) === String(this.direct.selectedUserId));
      if (chosen) {
        this.direct.userId = String(chosen.id);
        this.direct.userName = chosen.name;
      } else {
        this.direct.userId = '';
        this.direct.userName = '';
      }
    },
    async sendBroadcast() {
      try {
        this.sending.broadcast = true;
        this.result.broadcast = null;

        const endpoint = this.broadcast.type === 'announcement'
          ? '/api/admin/notifications/announcement'
          : '/api/admin/notifications/system';

        const payload = this.broadcast.type === 'announcement'
          ? {
              title: this.broadcast.title,
              message: this.broadcast.message,
              announcementType: 'ADMIN_ANNOUNCEMENT',
              userRole: this.broadcast.userRole || undefined,
            }
          : {
              title: this.broadcast.title,
              message: this.broadcast.message,
              systemType: this.broadcast.systemType,
              userRole: this.broadcast.userRole || undefined,
            };

        const res = await axios.post(this.baseUrl + endpoint, payload, { headers: this.authHeaders });
        const ok = res?.data?.result?.status === 200 || res?.status === 200;
        this.result.broadcast = { success: ok, message: ok ? 'تم إرسال الإشعار بنجاح' : 'فشل إرسال الإشعار' };
      } catch (e) {
        this.result.broadcast = { success: false, message: e?.response?.data?.mesAr || 'حدث خطأ أثناء الإرسال' };
      } finally {
        this.sending.broadcast = false;
      }
    },

    async sendDirect() {
      try {
        this.sending.direct = true;
        this.result.direct = null;

        if (this.direct.method === 'byUser' && this.direct.type === 'welcome') {
          // استخدام endpoint موجود لإرسال ترحيب لمستخدم محدد
          const payload = {
            userId: String(this.direct.userId),
            userName: this.direct.userName,
            userRole: this.direct.userRole
          };
          const res = await axios.post(this.baseUrl + '/api/admin/notifications/welcome', payload, { headers: this.authHeaders });
          const ok = res?.data?.result?.status === 200 || res?.status === 200;
          this.result.direct = { success: ok, message: ok ? 'تم إرسال الإشعار' : 'فشل إرسال الإشعار' };
          return;
        }

        if (this.direct.method === 'byUser' && this.direct.type === 'call_test') {
          // إرسال إشعار جلسة تجريبي (SESSION_CALL) لمستخدم محدد
          const payload = {
            userId: String(this.direct.userId),
            userName: this.direct.userName,
            userRole: this.direct.userRole,
            notificationType: 'SESSION_CALL',
            title: this.direct.title,
            message: this.direct.message,
            // يمكن للباك إند توليد sessionId/token أو استخدام هذه الافتراضية للتجربة
            sessionId: 'call-test-' + (this.direct.userId || 'user'),
            channelName: 'callTestChannel',
            hasVideo: false,
          };
          const res = await axios.post(this.baseUrl + '/api/admin/notifications/welcome', payload, { headers: this.authHeaders });
          const ok = res?.data?.result?.status === 200 || res?.status === 200;
          this.result.direct = { success: ok, message: ok ? 'تم إرسال إشعار الاتصال التجريبي' : 'فشل إرسال الإشعار' };
          return;
        }

        if (this.direct.method === 'byToken' && this.direct.type === 'test') {
          // استخدام نقطة اختبار عامة لإرسال إشعار إلى Token محدد
          const url = this.baseUrl + '/api/public/send-notification?fcmToken=' + encodeURIComponent(this.direct.fcmToken);
          const res = await axios.get(url, { headers: this.authHeaders });
          const ok = res?.data?.result?.status === 200 || res?.status === 200;
          this.result.direct = { success: ok, message: ok ? 'تم إرسال الاختبار' : 'فشل إرسال الاختبار' };
          return;
        }

        this.result.direct = { success: false, message: 'إعدادات الإرسال غير صحيحة' };
      } catch (e) {
        this.result.direct = { success: false, message: e?.response?.data?.mesAr || 'حدث خطأ أثناء الإرسال' };
      } finally {
        this.sending.direct = false;
      }
    }
  }
};
</script>

<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor;
}
.btn {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 disabled:opacity-50 disabled:cursor-not-allowed;
}
.loader {
  @apply inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2;
}
</style>

